import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { DEFAULTS } from "@/data/contentRegistry";

const ContentContext = createContext({ content: {}, reload: () => {} });

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState({});

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      setContent(data || {});
    } catch {
      setContent({});
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const value = useMemo(() => ({ content, reload }), [content, reload]);

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
};

export const useContent = () => useContext(ContentContext);

/** Returns a stable getter `t(key)` and `img(key)` for reading content inside callbacks. */
export const useT2 = () => {
  const { content } = useContent();
  const t = (key, fallback) => {
    const v = content?.[key];
    if (v !== undefined && v !== null && v !== "") return v;
    if (DEFAULTS[key] !== undefined && DEFAULTS[key] !== "") return DEFAULTS[key];
    return fallback ?? "";
  };
  const img = (key, fallback) => {
    const raw = t(key, fallback);
    if (!raw) return "";
    if (raw.startsWith("/api/uploads/")) {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      return `${base}${raw}`;
    }
    return raw;
  };
  return { t, img };
};

/**
 * Returns the live editable string for `key`.
 * Falls back to the registry default, then to `fallback`, then to "".
 */
export const useT = (key, fallback) => {
  const { content } = useContent();
  const value = content?.[key];
  if (value !== undefined && value !== null && value !== "") return value;
  if (DEFAULTS[key] !== undefined && DEFAULTS[key] !== "") return DEFAULTS[key];
  return fallback ?? "";
};

/** Image variant — returns URL/path. Resolves /api/uploads paths to absolute URLs when needed. */
export const useImg = (key, fallback) => {
  const raw = useT(key, fallback);
  if (!raw) return "";
  if (raw.startsWith("/api/uploads/")) {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}${raw}`;
  }
  return raw;
};
